import { createStore, StoreCore } from "@priolo/jon"
import farmsApi from "api/farms"
import routeSo from "stores/route/query"
import valuesSo from "stores/values"
import { Farm } from "types/Farm"
import { GrowUnit } from "types/GrowUnit"
import { Component, COMPONENT_TYPE, GrowUnitComponent } from "types/Service"
import { normalizeFarm } from "./utils"
import { Scopes } from "types/Task"
import copilotKitSo from "stores/copilotkit"



const isPLC = import.meta.env.VITE_TARGET == "plc"

/**
 * Gestione della lista delle FARMS
 */
const setup = {

	state: {
		/**  all the FARMs */
		all: <Farm[]>[],
		/** the selected FARMs */
		select: <Farm>null,

		/** gli SCOPE della FARM */
		scopes: <Scopes>null,

		/** il componente selezionato */
		componentSelect: <Component>null,
	},

	getters: {
		getFiltered: (_: void, store?: FarmStore): Farm[] => {
			let farms = [...store.state.all]
			farms = store.getFilteredExt(routeSo.getSearchUrl("search"))
			farms = routeSo.getSorted({ items: farms })
			return farms
		},
		getFilteredExt: (text: string, store?: FarmStore): Farm[] => {
			let farms = [...store.state.all]
			const txt = text?.trim().toLowerCase()
			if (txt && txt.length > 0) {
				farms = farms.filter(farm =>
					farm.name.toLowerCase().indexOf(txt) != -1
					|| farm.farmCode.toLowerCase().indexOf(txt) != -1
				)
			}
			return farms
		},
		/**  
		 * inserendo un array di id-farm restituisce una stringa con tutti i nomi delle famr
		 "farm bologna, farm1, farm2"
		*/
		getNames: (ids: number[], store?: FarmStore): string => ids
			?.map(id => store.state.all.find(farm => farm.id == id)?.name)
			.filter(name => name)
			.join(", ") ?? "<nothing>",

		getById: (id: number, store?: FarmStore): Farm => store.state.all.find(farm => farm.id == id),

		getByIds: (ids: number[], store?: FarmStore): Farm[] => ids.reduce((acc, id) => {
			const farm = store.getById(id)
			if (farm) acc.push(farm)
			return acc
		}, []),

		/** supponendo che il code delle GROWUNIT sia univoco per tutte le FARM. Restituisce la farm che contiene una specifica GROWUNIT  */
		getByGrowunitCode: (code: string, store?: FarmStore) => {
			if (!code) return null
			for (const farm of store.state.all) {
				const component = farm?.edgeServicesConfig?.components?.find(c => c.code == code && c.type == COMPONENT_TYPE.GROWUNIT)
				if (!!component) return farm
			}
			return null
		},
		/** restituisce tutte le FARM che contengono il parziale del componente passato */
		getByPartialComponent: (component: Partial<Component>, store?: FarmStore): { farm: Farm, components: Component[] }[] => {
			if (!component || !store.state.all) return []
			return store.state.all.reduce((acc, farm) => {
				const comopnents = farm?.edgeServicesConfig?.components?.filter(c => {
					let match = true
					if (component.code) match = match && c.code == component.code
					if (component.type) match = match && c.type == component.type
					if (component.name) match = match && c.name == component.name
					return match
				}) ?? []
				if (comopnents.length > 0) acc.push({ farm, comopnents })
				return acc
			}, [])
		},

		getDetails: (farm: Farm, store?: FarmStore) => `${farm.farmCode} ${farm.address} (${farm.zipCode}) ${farm.country}`,

		getGrowUnit: (id: number, store?: FarmStore): GrowUnit => id && store.state.select?.growUnits?.find(g => g.id == id),
		getGrowUnitByCode: (code: string, store?: FarmStore) => !!code && store.state.select?.growUnits?.find(g => g.growUnitCode == code),
		getGrowUnitByComponentSelect: (_: void, store?: FarmStore) => store.getGrowUnitByCode((<GrowUnitComponent>store.state.componentSelect)?.options?.code),
		getGrowUnitByComponentCode: (code: string, store?: FarmStore) => store.getGrowUnitByCode((<GrowUnitComponent>store.getComponentByCode(code))?.options?.code),


		/** il COMPONENT della FARM-SELECT con il "code" specificato */
		getComponentByCode: (code: string, store?: FarmStore) =>
			store.state.select?.edgeServicesConfig?.components?.find(c => c.code == code),
		/** tutti i COMPONENTS della FARM-SELECT con il "type" specificato  */
		getComponentsByType: (type: COMPONENT_TYPE, store?: FarmStore) =>
			store.state.select?.edgeServicesConfig?.components?.filter(c => c.type == type) ?? [],
		/** tutti i COMPONENTS della FARM-SELECT che sono in ascolto in una determinata coda NATS  */
		getComponentsByNats: (subject: string, store?: FarmStore) =>
			!!subject && store.state.select?.edgeServicesConfig?.components?.filter(c => c.subjects?.includes(subject) ?? false),
		/** il COMPONENT che gestisce la GROWUNIT identificata con il CODE */
		getComponentByGrowUnitCode: (code: string, store?: FarmStore) => {
			if (!code) return null
			return store.state.select?.edgeServicesConfig?.components?.find(s => s.type == COMPONENT_TYPE.GROWUNIT && (<GrowUnitComponent>s).options?.code == code)
		},

		/** sono in zero-os e ho la possibilità di accedere ai PLC-VALUES dal web */
		isInCloud: (_: void, store?: FarmStore) => !isPLC && !!store.state.select?.edgeServicesConfig?.cloud_nats
	},

	actions: {
		fetchAll: async (_: void, store?: FarmStore) => {
			const { data } = await farmsApi.index()
			store.setAll(data)
		},
		fetchAllIfVoid: async (_: void, store?: FarmStore) => {
			if (store.state.all?.length > 0) return
			await store.fetchAll()
		},
		fetchSelect: async (id: number, store?: FarmStore) => {

			// carico i dati della farm
			const { data: farm } = await farmsApi.get(id)

			// carico gli SCOPE della FARM (se è VIRTUAL non ci sono)
			store.state.scopes = {}
			if (id != 0) {
				const { data: scopes } = await farmsApi.scopes(id)
				store.state.scopes = scopes
			}

			store.setSelect(normalizeFarm(farm))
			console.log("FARM EDGE CONFIG:", farm.edgeServicesConfig)
			return farm
		},
		/**
		 * Carica la FARM selezionata SOLO SE non e' gia' caricata o e' diversa da quella richiesta
		 */
		fetchSelectIfVoid: async (id: number, store?: FarmStore) => {
			if (!!store.state.select && store.state.select.id == id) return store.state.select;
			return await store.fetchSelect(id)
		},


		//#region *** PLC ***

		/** preleva la FARM CORRENTE */
		fetchCurrent: async (_: void, store?: FarmStore) => {
			const { data: farm } = await farmsApi.current()
			store.setSelect(farm)
			document.title = `F2OS - ${farm?.farmCode ?? ""}`
			console.log("FARM EDGE CONFIG:", farm.edgeServicesConfig)
			return farm
		},
		fetchCurrentIfVoid: async (_: void, store?: FarmStore) => {
			if (store.state.select) return store.state.select
			return await store.fetchCurrent()
		},

		//#endregion

	},

	mutators: {
		setAll: (all: Farm[]) => ({ all }),
		setSelect: (select: Farm) => ({ select }),
		setComponentSelect: (componentSelect: Component) => {
			valuesSo.state.serviceCodeSel = componentSelect?.subjects?.[0]
			return { componentSelect }
		},
	},
}



export type FarmState = typeof setup.state
export type FarmGetters = typeof setup.getters
export type FarmActions = typeof setup.actions
export type FarmMutators = typeof setup.mutators
export interface FarmStore extends StoreCore<FarmState>, FarmGetters, FarmActions, FarmMutators {
	state: FarmState
}
const farmSo = createStore(setup) as FarmStore
export default farmSo

